import React from 'react';
import { FileText, Scale, UserCheck, CreditCard, Shield, AlertCircle } from 'lucide-react';
import '../styles/LegalPages.css';

const TermsPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">Terms of Use</h1>
        <p className="legal-updated">Last Updated: December 2025</p>

        <div className="legal-intro">
          <p>
            Welcome to Market Sentinel AI. These Terms of Use ("Terms") govern your access to and use of 
            the Market Sentinel AI mobile application and related services (collectively, the "Service"). 
            By accessing or using our Service, you agree to be bound by these Terms.
          </p>
          <p>
            <strong>PLEASE READ THESE TERMS CAREFULLY. IF YOU DO NOT AGREE TO THESE TERMS, 
            DO NOT USE THE SERVICE.</strong>
          </p>
        </div>

        <section className="legal-section">
          <div className="legal-section-header">
            <FileText size={24} />
            <h2>1. Acceptance of Terms</h2>
          </div>
          <div className="legal-content">
            <p>
              By creating an account, downloading, installing, or using Market Sentinel AI, you acknowledge 
              that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Market Sentinel AI. 
              We reserve the right to modify these Terms at any time. Continued use of the Service 
              after changes constitutes acceptance of the modified Terms.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <UserCheck size={24} />
            <h2>2. Eligibility and Account Registration</h2>
          </div>
          <div className="legal-content">
            <h3>2.1 Age Requirements</h3>
            <p>
              You must be at least 18 years old to use Market Sentinel AI. By using the Service, 
              you represent and warrant that you meet this age requirement and have the legal capacity 
              to enter into these Terms.
            </p>

            <h3>2.2 Account Creation</h3>
            <p>
              To access certain features, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>

            <h3>2.3 Account Restrictions</h3>
            <p>
              You may not:
            </p>
            <ul>
              <li>Create multiple accounts for the same individual</li>
              <li>Share your account credentials with others</li>
              <li>Use another person's account without permission</li>
              <li>Create an account using false or misleading information</li>
              <li>Transfer or sell your account to another party</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Shield size={24} />
            <h2>3. Service Description and Scope</h2>
          </div>
          <div className="legal-content">
            <h3>3.1 What We Provide</h3>
            <p>
              Market Sentinel AI provides automated market analysis and trading intelligence for:
            </p>
            <ul>
              <li>Cryptocurrency markets (BTC, ETH, and others)</li>
              <li>Forex currency pairs (EURUSD, GBPUSD, and others)</li>
              <li>Macro assets (SP500, Gold, DXY, and others)</li>
            </ul>
            <p>
              Our Service includes:
            </p>
            <ul>
              <li>Market narrative and analysis</li>
              <li>Opportunity detection algorithms</li>
              <li>AI-generated trade setups</li>
              <li>Watchtower alerts and notifications</li>
              <li>Multi-timeframe technical analysis</li>
            </ul>

            <h3>3.2 What We Do NOT Provide</h3>
            <p>
              <strong>IMPORTANT: Market Sentinel AI is an analytical tool only. We do NOT:</strong>
            </p>
            <ul>
              <li>Provide financial advice, investment advice, or trading recommendations</li>
              <li>Execute trades on your behalf</li>
              <li>Manage your funds or trading accounts</li>
              <li>Guarantee profits or trading success</li>
              <li>Act as a broker, dealer, or investment advisor</li>
            </ul>
            <p>
              All information provided is for educational and informational purposes only. 
              You are solely responsible for your trading decisions.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <CreditCard size={24} />
            <h2>4. Subscriptions and Payments</h2>
          </div>
          <div className="legal-content">
            <h3>4.1 Subscription Plans</h3>
            <p>
              Market Sentinel AI offers various subscription tiers with different features and access levels. 
              Subscription details, pricing, and features are presented within the app and at the time of purchase.
            </p>

            <h3>4.2 Billing and Renewals</h3>
            <ul>
              <li>Subscriptions are billed through your app store account (Google Play or Apple App Store)</li>
              <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
              <li>Prices are subject to change with notice</li>
              <li>No refunds for partial subscription periods</li>
              <li>You are responsible for managing your subscription through your app store settings</li>
            </ul>

            <h3>4.3 Free Trials</h3>
            <p>
              We may offer free trial periods for certain subscription plans. If you do not cancel before 
              the trial ends, you will be automatically charged for the subscription. Trial eligibility 
              may be limited to new users or specific conditions.
            </p>

            <h3>4.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your app store account settings. 
              Cancellation takes effect at the end of the current billing period. You will retain 
              access to premium features until that date.
            </p>

            <h3>4.5 Refund Policy</h3>
            <p>
              All subscription payments are processed through app stores and subject to their refund policies:
            </p>
            <ul>
              <li>Google Play: Refunds handled per Google's policy</li>
              <li>Apple App Store: Refunds handled per Apple's policy</li>
            </ul>
            <p>
              We do not have direct control over refunds. Please contact your app store for refund requests.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Scale size={24} />
            <h2>5. Acceptable Use Policy</h2>
          </div>
          <div className="legal-content">
            <h3>5.1 Permitted Use</h3>
            <p>
              You may use Market Sentinel AI only for lawful purposes and in accordance with these Terms.
            </p>

            <h3>5.2 Prohibited Activities</h3>
            <p>
              You agree NOT to:
            </p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Reverse engineer, decompile, or disassemble the app</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated systems (bots, scrapers) to access the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Impersonate others or provide false information</li>
              <li>Redistribute or resell our content or services</li>
              <li>Use the Service for market manipulation or illegal trading activity</li>
              <li>Extract or harvest data without permission</li>
            </ul>

            <h3>5.3 Consequences of Violation</h3>
            <p>
              Violation of these Terms may result in:
            </p>
            <ul>
              <li>Immediate suspension or termination of your account</li>
              <li>Legal action and pursuit of damages</li>
              <li>Reporting to law enforcement authorities</li>
              <li>Forfeiture of subscription fees without refund</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <AlertCircle size={24} />
            <h2>6. Intellectual Property Rights</h2>
          </div>
          <div className="legal-content">
            <h3>6.1 Our Rights</h3>
            <p>
              All content, features, and functionality of Market Sentinel AI, including but not limited to:
            </p>
            <ul>
              <li>Software code and algorithms</li>
              <li>Design, graphics, and user interface</li>
              <li>Text, analysis, and market commentary</li>
              <li>Trademarks, logos, and brand elements</li>
              <li>Data compilations and proprietary analysis</li>
            </ul>
            <p>
              ...are owned by Market Sentinel AI and protected by copyright, trademark, and other 
              intellectual property laws.
            </p>

            <h3>6.2 Limited License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to use 
              Market Sentinel AI for personal, non-commercial purposes, subject to these Terms.
            </p>

            <h3>6.3 Restrictions</h3>
            <p>
              You may not copy, modify, distribute, sell, or create derivative works from our Service 
              without explicit written permission.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>7. Disclaimers and Limitation of Liability</h2>
          <div className="legal-content">
            <h3>7.1 Service "AS IS"</h3>
            <p>
              MARKET SENTINEL AI IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS 
              FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3>7.2 No Guarantees</h3>
            <p>
              We do not guarantee that:
            </p>
            <ul>
              <li>The Service will be uninterrupted, secure, or error-free</li>
              <li>Analysis or alerts will be accurate or profitable</li>
              <li>Defects will be corrected promptly</li>
              <li>The Service will meet your specific requirements</li>
            </ul>

            <h3>7.3 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MARKET SENTINEL AI SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Trading losses or financial damages</li>
              <li>Lost profits or opportunities</li>
              <li>Data loss or corruption</li>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Damages exceeding the amount you paid for the Service in the past 12 months</li>
            </ul>

            <h3>7.4 Third-Party Content</h3>
            <p>
              We are not responsible for third-party content, services, or links accessed through our Service.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>8. Indemnification</h2>
          <div className="legal-content">
            <p>
              You agree to indemnify, defend, and hold harmless Market Sentinel AI, its officers, 
              directors, employees, and agents from any claims, damages, losses, liabilities, and 
              expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your trading decisions and financial outcomes</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>9. Termination</h2>
          <div className="legal-content">
            <h3>9.1 By You</h3>
            <p>
              You may terminate your account at any time by deleting the app and cancelling your subscription 
              through your app store account.
            </p>

            <h3>9.2 By Us</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time, with or without notice, for:
            </p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Suspected fraudulent or illegal activity</li>
              <li>Non-payment of subscription fees</li>
              <li>Any reason at our sole discretion</li>
            </ul>

            <h3>9.3 Effect of Termination</h3>
            <p>
              Upon termination:
            </p>
            <ul>
              <li>Your right to use the Service immediately ceases</li>
              <li>Your account data may be deleted</li>
              <li>No refunds will be provided for unused subscription time</li>
              <li>Sections of these Terms that should survive termination will remain in effect</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>10. Governing Law and Dispute Resolution</h2>
          <div className="legal-content">
            <h3>10.1 Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the jurisdiction 
              where Market Sentinel AI operates, without regard to conflict of law principles.
            </p>

            <h3>10.2 Dispute Resolution</h3>
            <p>
              Any disputes arising from these Terms or your use of the Service shall be resolved through:
            </p>
            <ul>
              <li>First, good faith negotiations between the parties</li>
              <li>If unresolved, binding arbitration in accordance with applicable arbitration rules</li>
              <li>Each party bears its own legal costs unless otherwise awarded</li>
            </ul>

            <h3>10.3 Class Action Waiver</h3>
            <p>
              You agree that disputes will be resolved individually, not as part of a class action or 
              collective proceeding.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>11. Modifications to Service</h2>
          <div className="legal-content">
            <p>
              We reserve the right to:
            </p>
            <ul>
              <li>Modify, suspend, or discontinue any aspect of the Service</li>
              <li>Change features, pricing, or subscription plans</li>
              <li>Update software and require app updates</li>
            </ul>
            <p>
              We will provide reasonable notice of material changes when possible, but are not obligated 
              to maintain any specific features indefinitely.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>12. General Provisions</h2>
          <div className="legal-content">
            <h3>12.1 Entire Agreement</h3>
            <p>
              These Terms, along with our Privacy Policy and Risk Disclaimer, constitute the entire 
              agreement between you and Market Sentinel AI.
            </p>

            <h3>12.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining 
              provisions will remain in full effect.
            </p>

            <h3>12.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision does not constitute a waiver of that right.
            </p>

            <h3>12.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms without our consent. We may assign these Terms 
              without restriction.
            </p>

            <h3>12.5 Force Majeure</h3>
            <p>
              We are not liable for delays or failures due to circumstances beyond our reasonable control.
            </p>
          </div>
        </section>

        <section className="legal-section legal-contact">
          <h2>Contact Information</h2>
          <div className="legal-content">
            <p>
              For questions about these Terms, please contact us:
            </p>
            <ul>
              <li>Website: https://market-sentinel.net</li>
              <li>Email: support@market-sentinel.net</li>
              <li>In-app support: Available through the mobile application</li>
            </ul>
          </div>
        </section>

        <div className="legal-acknowledgment">
          <p>
            <strong>BY USING MARKET SENTINEL AI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, 
            AND AGREE TO BE BOUND BY THESE TERMS OF USE.</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
